/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { PrimaryButton } from "../buttons/PrimaryButton";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
  Tooltip,
} from "@material-tailwind/react";
import DropdownFilter from "../modals/DropdownFilter";
import InformationIcon from "../../assets/icons/info/information.png";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import Chart from "react-apexcharts";
import { useUserStats } from "../../store/useUserStats";
import { useMarketPricing } from "../../store/useMarketPricing";
import { getStoreColor } from "../../constants/plasticColors";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import axios from "axios";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";

dayjs.extend(isoWeek);

const DEFAULT_STORE_CODES = ["VLS", "VBS", "EDP", "SMA"];
const ALL_MATERIALS_ID = "__ALL_MATERIALS__";

/** Matches formats: VLS, VLS_FS, 001_VLS_Ams_FS, VLS-Ams */
const isDefaultStore = (name: string) =>
  DEFAULT_STORE_CODES.some(
    (code) =>
      name === code ||
      name?.startsWith(`${code}_`) ||
      name?.startsWith(`${code}-`) ||
      new RegExp(`(^|_|-)${code}(_|-|$)`).test(name ?? "")
  );

const monthOptions = [
  { value: 1, label: "Jan" },
  { value: 2, label: "Feb" },
  { value: 3, label: "Mar" },
  { value: 4, label: "Apr" },
  { value: 5, label: "May" },
  { value: 6, label: "Jun" },
  { value: 7, label: "Jul" },
  { value: 8, label: "Aug" },
  { value: 9, label: "Sep" },
  { value: 10, label: "Oct" },
  { value: 11, label: "Nov" },
  { value: 12, label: "Dec" },
];

const quarterOptions = [
  { value: 1, label: "Q1" },
  { value: 2, label: "Q2" },
  { value: 3, label: "Q3" },
  { value: 4, label: "Q4" },
];

export const DroppersPerStoreChart = React.memo(() => {
  const snapshotRef = useRef(null);
  const {
    droppersPerStoreOverTime,
    getDroppersPerStoreOverTime,
    isDroppersPerStoreLoading,
    isDroppersPerStoreError,
  } = useUserStats();
  const { marketPrices, getAllMarketPrices } = useMarketPricing();
  const [_, takeScreenshot] = useScreenshot();
  const [isCsvDownloading, setIsCsvDownloading] = useState(false);

  const materialOptions = useMemo(() => {
    if (!marketPrices?.length) return [];
    const kgMaterials = marketPrices
      .filter((m) => m.measurement === "kg")
      .map((m) => ({ id: m._id!, name: m.name }));
    return [{ id: ALL_MATERIALS_ID, name: "All materials" }, ...kgMaterials];
  }, [marketPrices]);

  const [selectedMaterialId, setSelectedMaterialId] = useState<string | null>(
    null
  );
  const [fromToPeriod, setFromToPeriod] = useState<
    "month" | "week" | "quarter" | "year"
  >("month");
  const [startYear, setStartYear] = useState(() =>
    dayjs().subtract(12, "month").year()
  );
  const [startMonth, setStartMonth] = useState(
    () => dayjs().subtract(12, "month").month() + 1
  );
  const [endYear, setEndYear] = useState(() => dayjs().year());
  const [endMonth, setEndMonth] = useState(() => dayjs().month() + 1);
  const [startWeek, setStartWeek] = useState(() =>
    dayjs().subtract(12, "month").isoWeek()
  );
  const [startQuarter, setStartQuarter] = useState(1);
  const [endWeek, setEndWeek] = useState(() => dayjs().isoWeek());
  const [endQuarter, setEndQuarter] = useState(4);
  const [selectedLocations, setSelectedLocations] = useState<Set<string>>(
    new Set()
  );
  const seriesKeyRef = useRef("");
  const [droppersMetric, setDroppersMetric] = useState<
    "droppers" | "regular" | "5plus"
  >("droppers");

  const yearOptions = Array.from(
    { length: dayjs().year() - 2023 },
    (_, i) => dayjs().year() - i
  );
  const startMonthOptions = useMemo(() => {
    const cy = dayjs().year();
    const cm = dayjs().month() + 1;
    return startYear === cy
      ? monthOptions.filter((m) => m.value <= cm)
      : monthOptions;
  }, [startYear]);
  const endMonthOptions = useMemo(() => {
    const cy = dayjs().year();
    const cm = dayjs().month() + 1;
    return endYear === cy
      ? monthOptions.filter((m) => m.value <= cm)
      : monthOptions;
  }, [endYear]);
  const getWeekOptions = (y: number) => {
    const lastDay = dayjs(`${y}-12-31`);
    const totalWeeks = lastDay.isoWeek() === 1 ? 52 : lastDay.isoWeek();
    const currentWeek = dayjs().isoWeek();
    const currentY = dayjs().year();
    return Array.from({ length: totalWeeks }, (_, i) => {
      const w = i + 1;
      const start = dayjs().year(y).isoWeek(w).startOf("isoWeek");
      const end = dayjs().year(y).isoWeek(w).endOf("isoWeek");
      return {
        value: w,
        label: `Week ${w}: ${start.format("MMM DD")} - ${end.format("MMM DD")}`,
        disabled: y === currentY && w > currentWeek,
      };
    });
  };
  const startWeekOptions = useMemo(
    () => getWeekOptions(startYear),
    [startYear]
  );
  const endWeekOptions = useMemo(() => getWeekOptions(endYear), [endYear]);

  useEffect(() => {
    getAllMarketPrices();
  }, [getAllMarketPrices]);

  useEffect(() => {
    if (materialOptions.length > 0 && !selectedMaterialId) {
      const textilesOption =
        materialOptions.find(
          (m) =>
            m.id !== ALL_MATERIALS_ID &&
            m.name.toLowerCase().includes("textile")
        ) ?? materialOptions.find((m) => m.id !== ALL_MATERIALS_ID);
      if (textilesOption) {
        setSelectedMaterialId(textilesOption.id);
      }
    }
  }, [materialOptions, selectedMaterialId]);

  const downloadScreenshot = () => {
    takeScreenshot(snapshotRef.current).then((image: unknown) => {
      const link = document.createElement("a");
      link.href = image as string;
      link.download = "droppers_per_store.png";
      link.click();
    });
  };

  useEffect(() => {
    const effectiveMaterialId =
      selectedMaterialId && selectedMaterialId !== ALL_MATERIALS_ID
        ? selectedMaterialId
        : undefined;
    let apiStartYear: number;
    let apiStartMonth: number;
    let apiEndYear: number;
    let apiEndMonth: number;
    if (fromToPeriod === "week") {
      const startDate = dayjs()
        .year(startYear)
        .isoWeek(startWeek)
        .startOf("isoWeek");
      const endDate = dayjs().year(endYear).isoWeek(endWeek).endOf("isoWeek");
      apiStartYear = startDate.year();
      apiStartMonth = startDate.month() + 1;
      apiEndYear = endDate.year();
      apiEndMonth = endDate.month() + 1;
    } else if (fromToPeriod === "quarter") {
      apiStartYear = startYear;
      apiStartMonth = (startQuarter - 1) * 3 + 1;
      apiEndYear = endYear;
      apiEndMonth = endQuarter * 3;
    } else if (fromToPeriod === "year") {
      apiStartYear = startYear;
      apiStartMonth = 1;
      apiEndYear = endYear;
      apiEndMonth = 12;
    } else {
      apiStartYear = startYear;
      apiStartMonth = startMonth;
      apiEndYear = endYear;
      apiEndMonth = endMonth;
    }
    getDroppersPerStoreOverTime({
      period: fromToPeriod,
      startYear: apiStartYear,
      startMonth: apiStartMonth,
      endYear: apiEndYear,
      endMonth: apiEndMonth,
      materialId: effectiveMaterialId,
      metric: droppersMetric,
    });
  }, [
    fromToPeriod,
    selectedMaterialId,
    droppersMetric,
    startYear,
    startMonth,
    startWeek,
    startQuarter,
    endYear,
    endMonth,
    endWeek,
    endQuarter,
    getDroppersPerStoreOverTime,
  ]);

  const hasData =
    droppersPerStoreOverTime &&
    droppersPerStoreOverTime.categories?.length > 0 &&
    droppersPerStoreOverTime.series?.length > 0;

  const showLoading = isDroppersPerStoreLoading;

  const rawSeries = hasData ? droppersPerStoreOverTime.series : [];
  const categories = hasData ? droppersPerStoreOverTime.categories : [];

  useEffect(() => {
    if (!rawSeries.length) return;
    const key = rawSeries
      .map((s) => s.name)
      .sort()
      .join(",");
    if (key !== seriesKeyRef.current) {
      seriesKeyRef.current = key;
      const defaultNames = rawSeries
        .map((s) => s.name)
        .filter((n) => isDefaultStore(n));
      setSelectedLocations(new Set(defaultNames));
    }
  }, [rawSeries]);

  const filteredSeries = useMemo(() => {
    if (!rawSeries.length) return [];
    if (selectedLocations.size === 0) {
      return rawSeries.filter((s) => isDefaultStore(s.name));
    }
    return rawSeries.filter((s) => selectedLocations.has(s.name));
  }, [rawSeries, selectedLocations]);

  const locationOptions = useMemo(
    () => rawSeries.map((s) => s.name),
    [rawSeries]
  );
  const selectedLocationsArr = useMemo(
    () => Array.from(selectedLocations),
    [selectedLocations]
  );
  const handleLocationToggle = (locationName: string) => {
    setSelectedLocations((prev) => {
      const next = new Set(prev);
      if (next.has(locationName)) next.delete(locationName);
      else next.add(locationName);
      return next;
    });
  };

  const series = filteredSeries;
  const locationColors = useMemo(
    () => series.map((s) => getStoreColor(s.name)),
    [series]
  );

  const maxYValue = useMemo(() => {
    if (!series.length) return undefined;
    let max = 0;
    for (const s of series) {
      const dataArr = (s.data as (number | null | undefined)[]) ?? [];
      for (const v of dataArr) {
        if (typeof v === "number" && !Number.isNaN(v) && v > max) {
          max = v;
        }
      }
    }
    if (max === 0) return undefined;
    // round up to nearest 5 for nicer axis
    return Math.ceil(max / 5) * 5;
  }, [series]);

  const downloadCsv = () => {
    if (!hasData) return;
    setIsCsvDownloading(true);
    const buildDailyParams = () => {
      const effectiveMaterialId =
        selectedMaterialId && selectedMaterialId !== ALL_MATERIALS_ID
          ? selectedMaterialId
          : undefined;
      if (fromToPeriod === "week") {
        const startDate = dayjs().year(startYear).isoWeek(startWeek).startOf("isoWeek");
        const endDate = dayjs().year(endYear).isoWeek(endWeek).endOf("isoWeek");
        return {
          params: {
            startYear: startDate.year(),
            startMonth: startDate.month() + 1,
            endYear: endDate.year(),
            endMonth: endDate.month() + 1,
            period: "day",
            ...(effectiveMaterialId ? { materialId: effectiveMaterialId } : {}),
            ...(droppersMetric ? { metric: droppersMetric } : {}),
          },
        };
      }

      return {
        params: {
          startYear,
          startMonth:
            fromToPeriod === "year"
              ? 1
              : fromToPeriod === "quarter"
                ? (startQuarter - 1) * 3 + 1
                : startMonth,
          endYear,
          endMonth:
            fromToPeriod === "year"
              ? 12
              : fromToPeriod === "quarter"
                ? endQuarter * 3
                : endMonth,
          period: "day",
          ...(effectiveMaterialId ? { materialId: effectiveMaterialId } : {}),
          ...(droppersMetric ? { metric: droppersMetric } : {}),
        },
      };
    };

    axios
      .get("/users/droppers-per-store", { params: buildDailyParams().params })
      .then((response) => {
        const dailyCategories = (response.data?.categories as string[]) ?? [];
        const dailySeries = (response.data?.series as typeof series) ?? [];
        const header =
          "Date," +
          dailySeries.map((s) => String(s.name ?? "").replace(/,/g, " ")).join(",") +
          "\n";
        const rows = dailyCategories.map((label, idx) => {
          const values = dailySeries.map((s) => {
            const dataArr = (s.data as (number | null | undefined)[]) ?? [];
            const val = dataArr[idx];
            return val != null ? String(val) : "";
          });
          return [label, ...values].join(",");
        });
        const csv = header + rows.join("\n");
        const blob = new Blob([csv], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `droppers_per_store_daily_${dayjs().format("YYYY-MM-DD")}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error("Error downloading CSV for DroppersPerStore:", error);
      })
      .finally(() => {
        setIsCsvDownloading(false);
      });
  };

  return (
    <div className="flex flex-col w-full p-2 sm:p-7 bg-white  border-gray-border rounded-xl shadow-none mb-1 sm:mb-10">
      <div className="flex flex-col sm:flex-row w-full items-start justify-between">
        <div></div>
        <div className="flex flex-col sm:flex-row sm:flex-wrap w-full min-w-0 items-start sm:items-center justify-start sm:justify-end gap-2 sm:gap-3">
          {/* Material selector - styled like MaterialVolumesPerMaterial and placed first */}
          <div className="flex items-center gap-2">
            <span className="font-satoshi text-sm font-medium text-gray-700 whitespace-nowrap">
              Material:
            </span>
            <Menu>
              <MenuHandler>
                <button className="h-9 px-3 sm:px-4 border-2 rounded-md border-gray-300 bg-gray-50 hover:bg-gray-100 flex items-center justify-center font-satoshi font-semibold text-gray-800 text-sm whitespace-nowrap min-w-[160px] shadow-sm">
                  {materialOptions.find((m) => m.id === selectedMaterialId)
                    ?.name ?? "Select material"}
                  <ChevronDown className="ml-1 sm:ml-2 w-4 h-4 flex-shrink-0" />
                </button>
              </MenuHandler>
              <MenuList
                className="rounded-none max-h-[300px] overflow-y-auto"
                placeholder={undefined}
              >
                {materialOptions.map((m) => (
                  <MenuItem
                    key={m.id}
                    disabled={selectedMaterialId === m.id}
                    onClick={() => setSelectedMaterialId(m.id)}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {m.name}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </div>

          {locationOptions.length > 0 && (
            <DropdownFilter
              label={`Locations (${selectedLocationsArr.length})`}
              options={locationOptions}
              selectedOptions={selectedLocationsArr}
              onSelect={handleLocationToggle}
            />
          )}
          <div className="flex items-center gap-1 sm:gap-2">
            <label className="font-satoshi text-xs sm:text-sm text-gray-text whitespace-nowrap">
              View:
            </label>
            <Menu>
              <MenuHandler>
                <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                  {droppersMetric === "droppers"
                    ? "Droppers"
                    : droppersMetric === "regular"
                      ? "2+ droppers"
                      : "5+ droppers"}
                  <ChevronDown className="ml-1 sm:ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                <MenuItem
                  disabled={droppersMetric === "droppers"}
                  onClick={() => setDroppersMetric("droppers")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  Droppers
                </MenuItem>
                <MenuItem
                  disabled={droppersMetric === "regular"}
                  onClick={() => setDroppersMetric("regular")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  2+ droppers
                </MenuItem>
                <MenuItem
                  disabled={droppersMetric === "5plus"}
                  onClick={() => setDroppersMetric("5plus")}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  5+ droppers
                </MenuItem>
              </MenuList>
            </Menu>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <Menu>
              <MenuHandler>
                <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                  {fromToPeriod === "month"
                    ? "Monthly"
                    : fromToPeriod === "week"
                      ? "Weekly"
                      : fromToPeriod === "quarter"
                        ? "Quarterly"
                        : "Yearly"}
                  <ChevronDown className="ml-1 sm:ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                {(["month", "week", "quarter", "year"] as const).map((p) => (
                  <MenuItem
                    key={p}
                    disabled={fromToPeriod === p}
                    onClick={() => setFromToPeriod(p)}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {p === "month"
                      ? "Monthly"
                      : p === "week"
                        ? "Weekly"
                        : p === "quarter"
                          ? "Quarterly"
                          : "Yearly"}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </div>
          <div className="flex items-center gap-2">
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {startYear}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                {yearOptions.map((y) => (
                  <MenuItem
                    key={y}
                    disabled={startYear === y}
                    onClick={() => setStartYear(y)}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {y}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
            {fromToPeriod === "month" && (
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                    {startMonthOptions.find((m) => m.value === startMonth)
                      ?.label || "Month"}
                    <ChevronDown className="ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {startMonthOptions.map((m) => (
                    <MenuItem
                      key={m.value}
                      disabled={startMonth === m.value}
                      onClick={() => setStartMonth(m.value)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {m.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
            {fromToPeriod === "week" && (
              <Menu>
                <MenuHandler>
                  <button
                    className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm max-w-[180px] sm:max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                    title={
                      startWeekOptions.find((w) => w.value === startWeek)?.label
                    }
                  >
                    {startWeekOptions.find((w) => w.value === startWeek)
                      ?.label || "Week"}
                    <ChevronDown className="ml-1 sm:ml-2" />
                  </button>
                </MenuHandler>
                <MenuList
                  className="rounded-none max-h-[280px] overflow-y-auto"
                  placeholder={undefined}
                >
                  {startWeekOptions.map((w) => (
                    <MenuItem
                      key={w.value}
                      disabled={startWeek === w.value}
                      onClick={() => setStartWeek(w.value)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {w.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
            {fromToPeriod === "quarter" && (
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                    {
                      quarterOptions.find((q) => q.value === startQuarter)
                        ?.label
                    }
                    <ChevronDown className="ml-1 sm:ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {quarterOptions.map((q) => (
                    <MenuItem
                      key={q.value}
                      disabled={startQuarter === q.value}
                      onClick={() => setStartQuarter(q.value)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {q.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
          </div>
          <div className="flex items-center gap-2">
            <label className="font-satoshi text-sm text-gray-text">To:</label>
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {endYear}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                {yearOptions.map((y) => (
                  <MenuItem
                    key={y}
                    disabled={endYear === y}
                    onClick={() => setEndYear(y)}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {y}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
            {fromToPeriod === "month" && (
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                    {endMonthOptions.find((m) => m.value === endMonth)?.label ||
                      "Month"}
                    <ChevronDown className="ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {endMonthOptions.map((m) => (
                    <MenuItem
                      key={m.value}
                      disabled={endMonth === m.value}
                      onClick={() => setEndMonth(m.value)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {m.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
            {fromToPeriod === "week" && (
              <Menu>
                <MenuHandler>
                  <button
                    className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm max-w-[180px] sm:max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                    title={
                      endWeekOptions.find((w) => w.value === endWeek)?.label
                    }
                  >
                    {endWeekOptions.find((w) => w.value === endWeek)?.label ||
                      "Week"}
                    <ChevronDown className="ml-1 sm:ml-2" />
                  </button>
                </MenuHandler>
                <MenuList
                  className="rounded-none max-h-[280px] overflow-y-auto"
                  placeholder={undefined}
                >
                  {endWeekOptions.map((w) => (
                    <MenuItem
                      key={w.value}
                      disabled={endWeek === w.value}
                      onClick={() => setEndWeek(w.value)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {w.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
            {fromToPeriod === "quarter" && (
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                    {quarterOptions.find((q) => q.value === endQuarter)?.label}
                    <ChevronDown className="ml-1 sm:ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {quarterOptions.map((q) => (
                    <MenuItem
                      key={q.value}
                      disabled={endQuarter === q.value}
                      onClick={() => setEndQuarter(q.value)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {q.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            )}
          </div>

          <PrimaryButton
            size="sm"
            onClick={downloadCsv}
            disabled={!hasData || isCsvDownloading}
            label={isCsvDownloading ? "..." : "Download CSV"}
            className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
          />
          <PrimaryButton
            size="sm"
            onClick={downloadScreenshot}
            label="Download image"
            className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
          />
        </div>
      </div>
      <div ref={snapshotRef} className="relative min-h-[320px] mt-4">
        <div className="flex items-center ">
          <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black">
            Droppers per store
          </p>
          <Tooltip
            content={
              droppersMetric === "droppers"
                ? "Shows the number of users who dropped the selected material at each store (>= 1 drop). Uses drop location at time of drop (historical data preserved)."
                : droppersMetric === "regular"
                  ? "Shows the number of 2+ droppers per store (users with 2 or more drops) for the selected material and period."
                  : "Shows the number of heavy droppers per store (users with 5 or more drops) for the selected material and period."
            }
            placeholder={undefined}
          >
            <img
              src={InformationIcon}
              alt="info"
              className="inline-block w-4 h-4 ml-2 cursor-pointer"
            />
          </Tooltip>
        </div>
        {showLoading ? (
          <div className="flex items-center justify-center h-[300px] w-full">
            <svg
              aria-hidden="true"
              className="w-8 h-8 text-gray-200 animate-spin fill-primary-base"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
          </div>
        ) : isDroppersPerStoreError ? (
          <div className="w-full h-[300px] flex items-center justify-center">
            <p className="text-red-500 font-satoshi">
              Failed to load data. Please try again.
            </p>
          </div>
        ) : hasData ? (
          <Chart
            key={`${droppersMetric}-${selectedMaterialId}-${fromToPeriod}-${startYear}-${startMonth}-${endYear}-${endMonth}-${series
              .map((s) => s.name)
              .join(",")}`}
            {...chartConfig}
            options={{
              ...chartConfig?.options,
              xaxis: {
                ...chartConfig?.options?.xaxis,
                categories,
              },
              yaxis: {
                ...chartConfig?.options?.yaxis,
                min: 0,
                max: maxYValue,
                title: {
                  text:
                    droppersMetric === "droppers"
                      ? "Number of droppers"
                      : droppersMetric === "regular"
                        ? "Number of 2+ droppers"
                        : "Number of 5+ droppers",
                  style: {
                    fontSize: "12px",
                    fontFamily: "Satoshi, sans-serif",
                    fontWeight: 500,
                  },
                },
              },
              colors: locationColors.length > 0 ? locationColors : undefined,
            }}
            series={series}
          />
        ) : (
          <div className="w-full h-[300px] flex items-center justify-center">
            <p className="text-gray-500 font-satoshi">
              No data available for the selected material and filters.
            </p>
          </div>
        )}
      </div>
    </div>
  );
});

const chartConfig = {
  type: "line" as const,
  width: "100%",
  height: 300,
  options: {
    chart: {
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
        speed: 300,
        animateGradually: {
          enabled: true,
          delay: 50,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 150,
        },
      },
    },
    legend: {
      show: true,
      position: "right" as const,
      horizontalAlign: "left" as const,
      fontSize: "12px",
      fontFamily: "Satoshi, sans-serif",
      itemMargin: { horizontal: 8, vertical: 4 },
    },
    title: {
      text: "",
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth" as const,
      width: 2,
    },
    xaxis: {
      axisTicks: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
      labels: {
        style: {
          colors: "#374151",
          fontSize: "12px",
          fontFamily: "Satoshi, sans-serif",
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: "#374151",
          fontSize: "12px",
          fontFamily: "Satoshi, sans-serif",
        },
      },
    },
    grid: {
      show: true,
      borderColor: "#dddddd",
      strokeDashArray: 5,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
      padding: {
        top: 5,
        right: 40,
      },
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      theme: "dark",
      shared: true,
      intersect: false,
      custom: function ({
        series,
        seriesIndex,
        dataPointIndex,
        w,
      }: {
        series: number[][];
        seriesIndex: number;
        dataPointIndex: number;
        w: any;
      }) {
        const category = w.globals.labels?.[dataPointIndex] ?? "";
        const items = series
          .map((s: number[], idx: number) => ({
            name: w.globals.seriesNames?.[idx] ?? "",
            value: s[dataPointIndex] ?? 0,
            color: w.config.colors?.[idx] ?? "#808080",
          }))
          .filter((item: { value: number }) => item.value > 0)
          .sort(
            (a: { value: number }, b: { value: number }) => b.value - a.value
          );
        let html = `<div style="color: #000;">`;
        html += `<div class="apexcharts-tooltip-title" style="color: #fff; font-family: Satoshi, sans-serif; font-size: 12px; font-weight: 600; padding: 8px 12px;">${category}</div>`;
        html += `<div style="padding: 4px 12px 8px 12px; color: #fff;">`;
        items.forEach(
          (item: { name: string; value: number; color: string }) => {
            const formatted = new Intl.NumberFormat("nl-NL", {
              style: "decimal",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(item.value);
            html += `
            <div style="display: flex; align-items: center; gap: 8px; margin-top: 4px; color: #fff;">
              <span style="width: 12px; height: 12px; border-radius: 50%; background: ${item.color};"></span>
              <span style="font-family: Satoshi, sans-serif; font-size: 12px; color: #fff;">${item.name}: ${formatted}</span>
            </div>
          `;
          }
        );
        html += `</div></div>`;
        return html;
      },
    },
  },
};
